import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, Search, X } from 'lucide-react';
import type { RunLogLine } from '@/hooks/useRunStream';

/**
 * LogStreamPane - Live-Output-Pane für Run-Detail (Sprint 5, WT-506).
 *
 * Features:
 *  - Auto-Scroll mit Toggle (User pausiert beim Lesen, Pane bleibt stehen,
 *    bis er wieder unten ist - klassisches "Tail -f"-Verhalten).
 *  - Color-Coding: stdout normal, stderr rot, ansible-events (host_ok/...)
 *    mit Sub-Icon.
 *  - Filter-Toolbar: Host-Multi-Select (chipsenes), Severity-Filter,
 *    Volltextsuche (case-insensitive substring).
 *  - Bewusst KEINE virtualisierte Liste (zu wenig Komplexität wert): Wir
 *    cappen Lines auf 5000 im Hook und zeigen einen "older lines truncated"-
 *    Hinweis. Der Browser hält 5000 Zeilen problemlos.
 *
 * A11y: Live-Output-Element nutzt aria-live="polite" NICHT - sonst spammt
 * der Screen-Reader bei jeder Zeile. Stattdessen melden wir nur Status-
 * Wechsel über den Run-Detail-Header (siehe RunDetailRoute).
 */

interface Props {
  lines: RunLogLine[];
  /** Hosts, die in den Lines vorkamen (für Filter-Toolbar). */
  hosts?: string[];
  /** Pause/Play wird auch nach außen gesignalisiert. */
  onPauseChange?: (paused: boolean) => void;
  /** Optional: max-Höhe als Tailwind-Klasse. Default h-[480px]. */
  heightClass?: string;
  /** Externer "Pane is full" Hint - kommt vom Backend wenn Output truncated. */
  truncatedNotice?: string | null;
}

type LevelFilter = 'all' | 'warn+' | 'error';

export function LogStreamPane({
  lines,
  hosts = [],
  onPauseChange,
  heightClass = 'h-[480px]',
  truncatedNotice,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [hostFilter, setHostFilter] = useState<string>('all');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lines.filter((line) => {
      if (hostFilter !== 'all' && line.host !== hostFilter) return false;
      if (levelFilter === 'error' && line.level !== 'error') return false;
      if (levelFilter === 'warn+' && line.level !== 'warning' && line.level !== 'error') {
        return false;
      }
      if (q) {
        const hay = `${line.host ?? ''} ${line.message}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [lines, hostFilter, levelFilter, search]);

  // Auto-Scroll: solange paused=false und User in der Nähe vom Bottom ist,
  // halten wir die Liste am Ende fest.
  useEffect(() => {
    if (paused) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [filtered.length, paused]);

  // Pause toggle exportieren.
  useEffect(() => {
    onPauseChange?.(paused);
  }, [paused, onPauseChange]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-2 p-2">
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-surface px-2 text-xs hover:bg-surface-2"
          aria-pressed={paused}
          data-testid="logpane-pause"
        >
          {paused ? (
            <>
              <Play className="size-3" aria-hidden="true" /> Fortsetzen
            </>
          ) : (
            <>
              <Pause className="size-3" aria-hidden="true" /> Pausieren
            </>
          )}
        </button>

        <label className="flex items-center gap-1 text-xs">
          <span className="text-muted">Host:</span>
          <select
            value={hostFilter}
            onChange={(e) => setHostFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-xs"
            data-testid="logpane-host-filter"
          >
            <option value="all">Alle ({hosts.length})</option>
            {hosts.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1 text-xs">
          <span className="text-muted">Level:</span>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LevelFilter)}
            className="rounded-md border border-border bg-surface px-2 py-1 text-xs"
            data-testid="logpane-level-filter"
          >
            <option value="all">Alle</option>
            <option value="warn+">Warning+</option>
            <option value="error">Error only</option>
          </select>
        </label>

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2 top-1.5 size-3 text-muted" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche im Output ..."
            aria-label="Im Output suchen"
            className="rounded-md border border-border bg-surface px-7 py-1 text-xs"
            data-testid="logpane-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-1 top-1 rounded p-0.5 text-muted hover:bg-surface-2"
              aria-label="Suche zurücksetzen"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          )}
        </div>

        <span
          className="ml-1 text-xs text-muted"
          data-testid="logpane-count"
          aria-live="off"
        >
          {filtered.length} / {lines.length}
        </span>
      </div>

      <div
        ref={containerRef}
        role="log"
        aria-label="Run-Output"
        className={[
          'overflow-y-auto rounded-md border border-border bg-[#0b0d12] p-2 font-mono text-xs leading-snug text-zinc-200',
          heightClass,
        ].join(' ')}
        data-testid="logpane-output"
      >
        {truncatedNotice && (
          <div className="mb-1 rounded border border-warning/30 bg-warning/10 px-2 py-1 text-[11px] text-warning">
            {truncatedNotice}
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="text-muted">(noch kein Output)</div>
        ) : (
          filtered.map((line) => <LogLineRow key={line.seq} line={line} />)
        )}
      </div>
    </div>
  );
}

const LEVEL_CLASS: Record<RunLogLine['level'], string> = {
  debug: 'text-zinc-500',
  info: 'text-zinc-200',
  warning: 'text-amber-300',
  error: 'text-red-400',
};

const SOURCE_PREFIX: Record<RunLogLine['source'], string> = {
  stdout: ' ',
  stderr: '!',
  event: '·',
};

function LogLineRow({ line }: { line: RunLogLine }): JSX.Element {
  const levelClass = LEVEL_CLASS[line.level];
  const sourcePrefix = SOURCE_PREFIX[line.source];
  const hostBadge = line.host ? `[${line.host}] ` : '';
  const eventBadge = line.event ? ` (${line.event}) ` : '';
  return (
    <div className={['whitespace-pre-wrap break-words', levelClass].join(' ')}>
      <span className="select-none text-zinc-600" aria-hidden="true">
        {sourcePrefix}{' '}
      </span>
      <span className="text-zinc-500">{hostBadge}</span>
      {line.event && <span className="text-cyan-400">{eventBadge}</span>}
      <span>{line.message}</span>
    </div>
  );
}
